import { useIsMobile } from '@ben/orion/hooks/use-is-mobile';
import { memo, useId } from 'react';
import type { PhoneInputProps } from 'react-phone-input-2';
import ReactPhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import styled, { css, useTheme } from 'styled-components';
import type { ITheme } from '../../utils/theme';
import InputFeedback from '../input-feedback';
import InputLabelWithHint from '../input-label/input-label-with-hint';
import { getCurrencyColor } from '../input/helper-functions/currency';
import { getPhoneInputBorderColor } from './utils/get-phone-input-border-color';

export interface IPhoneInput extends PhoneInputProps {
  onChange?: (phoneNumber: string) => void;
  hasError?: boolean;
  errorMessage?: string;
  name?: string;
  id?: string;
  width?: string;
}

const PhoneInput = ({
  inputProps,
  inputStyle,
  dropdownStyle,
  buttonStyle,
  searchStyle,
  excludeCountries,
  hasError = false,
  errorMessage,
  name = '',
  disabled = false,
  id = '',
  width,
  ...props
}: IPhoneInput) => {
  const defaultId = useId();
  const phoneInputId = id || defaultId;
  const theme = useTheme();
  const isMobile = useIsMobile();

  // font size of 16px on mobile to avoid zoom
  const fontStyles = isMobile
    ? { ...theme.typography.bodyMd1, fontSize: '16px' }
    : theme.typography.bodyMd1;

  const inputStyles = {
    width: width || theme.input.text.width,
    border: getPhoneInputBorderColor(hasError, disabled, theme),
    color: getCurrencyColor(hasError, disabled, theme),
    height: theme.input.text.height,
    paddingLeft: theme.spacing(10),
    borderRadius: theme.borderRadius.sm,
    boxShadow: 'none',
    background: disabled
      ? theme.palette.neutral['50']
      : theme.palette.neutral['00'],
    ...fontStyles,
  };

  return (
    <Wrapper $width={width}>
      <InputLabelWithHint
        name={name}
        hasError={hasError}
        showLabel={Boolean(name)}
        inputId={`phone-input-${phoneInputId}`}
      />
      <StyledReactPhoneInput
        disabled={disabled}
        $hasError={hasError}
        inputProps={{
          'aria-label': `${name} input`,
          ...inputProps,
        }}
        inputStyle={{
          ...inputStyles,
          ...inputStyle,
        }}
        dropdownStyle={{
          maxHeight: '400px',
          border: `1px solid ${theme.palette.neutral[60]}`,
          borderRadius: theme.borderRadius.sm,
          boxShadow: 'none',
          marginTop: theme.spacing(0.5),
          ...fontStyles,
          ...dropdownStyle,
        }}
        buttonStyle={{
          width: theme.spacing(8),
          background: 'transparent',
          border: 0,
          ...buttonStyle,
        }}
        searchStyle={{
          textTransform: 'capitalize',
          padding: theme.spacing(1),
          border: 0,
          width: '100%',
          ...fontStyles,
          ...searchStyle,
        }}
        country="gb"
        placeholder=""
        enableSearch
        enableTerritories
        excludeCountries={excludeCountries ?? ['je', 'ru']}
        enableAreaCodes={['do']}
        addInternationalOption={false}
        disableSearchIcon
        searchPlaceholder="Search countries…"
        id={`phone-input-${phoneInputId}`}
        maximumScale={1}
        {...props}
      />
      <InputFeedback errorMessage={hasError ? errorMessage : undefined} />
    </Wrapper>
  );
};

export default memo(PhoneInput);

export const StyledReactPhoneInput = styled((props) => (
  <ReactPhoneInput {...props} />
))`
  ${({ theme, $hasError }: { theme: ITheme; $hasError: boolean }) => css`
    .flag-dropdown {
      width: ${theme.spacing(9)} !important;

      &:before {
        content: '';
        position: absolute;
        right: 0;
        bottom: 0px;
        top: 4px;
        width: 1px;
        height: 32px;
        background-color: #d3d3d3;
      }
    }

    .flag-dropdown:before {
      height: 20px;
      top: 10px;
    }

    .selected-flag {
      width: ${theme.spacing(9)} !important;
      background: transparent !important;
      padding-left: ${theme.spacing(2)} !important;
      box-shadow: none !important;

      .arrow {
        margin-left: ${theme.spacing(0.5)};
        margin-top: -3px;
        width: 0.4em;
        height: 0.4em;
        border-left: none !important;
        border-right: 2px solid ${theme.palette.primary.main} !important;
        border-top: 2px solid ${theme.palette.primary.main} !important;
        transform: rotate(135deg) !important;

        &.up {
          transform: rotate(315deg) !important;
          transform-origin: 100% 50%;
          border-right: 2px solid ${theme.palette.primary.main} !important;
          border-top: 2px solid ${theme.palette.primary.main} !important;
          border-bottom: none !important;
        }
      }
    }

    .selected-flag:focus,
    .react-tel-input .selected-flag.open:before,
    & > .form-control:focus {
      outline: 2px solid
        ${$hasError ? theme.palette.error.main : theme.palette.neutral[100]} !important;
      box-shadow: none !important;
      border: none !important;
    }

    .react-tel-input .selected-flag:focus:before {
      outline: 2px solid
        ${$hasError ? theme.palette.error.main : theme.palette.neutral[100]} !important;
      box-shadow: none !important;
      border-color: transparent !important;
    }

    .react-tel-input .selected-flag.open:before {
      outline: 2px solid
        ${$hasError ? theme.palette.error.main : theme.palette.neutral[100]} !important;
      box-shadow: none !important;
      border-color: transparent !important;
    }

    .flag-dropdown {
      box-shadow: none !important;
    }

    .search {
      padding: ${theme.spacing(0.5)} !important;
    }

    .country-list {
      .flag {
        display: none !important;
      }

      .country {
        padding: ${theme.spacing(1, 2)} !important;
      }
    }
  `};
`;

const Wrapper = styled.div`
  ${({ $width }: { $width?: string }) => css`
    display: flex;
    flex-direction: column;
    width: ${$width};
  `}
`;
